import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, Plug, UploadSimple, ChartBar, ArrowRight } from "phosphor-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useSharedProfile } from "../context/SharedProfileContext";

export const TRADES_EXCHANGE_ANNOUNCEMENT_STORAGE_KEY = "geni_trades_exchange_import_announcement_v1";

export default function TradesExchangeAnnouncementModal() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { isSharedProfileView, isPublicProfileView } = useSharedProfile();

  useEffect(() => {
    // Don't show if auth is loading or user is not logged in
    if (loading || !user) {
      setIsOpen(false);
      return;
    }

    // Don't show in shared or public profile views
    if (isSharedProfileView || isPublicProfileView) {
      setIsOpen(false);
      return;
    }

    // Check if current route is part of GENI Trades
    // GENI Trades routes include /dashboard, /positions, /history, /analytics, /advanced, /performance, /exchange-trades, /exchange-import, /holdings, /insights, etc.
    // Exclude options routes (/options, /options/*), auth routes, landing, shared/public profiles
    const path = location.pathname;
    const isOptionsRoute = path.startsWith("/options");
    const isPublicStaticRoute =
      path === "/" ||
      path === "/login" ||
      path === "/signup" ||
      path === "/forgot-password" ||
      path === "/auth/confirm" ||
      path === "/auth/reset-password" ||
      path === "/terms" ||
      path === "/privacy" ||
      path === "/about" ||
      path === "/what-is-geni" ||
      path === "/blog" ||
      path.startsWith("/share/") ||
      path.startsWith("/shared/") ||
      path.startsWith("/profile/") ||
      path.startsWith("/u/") ||
      path.startsWith("/positions/") ||
      path.startsWith("/pt/");

    const isTradesRoute = !isOptionsRoute && !isPublicStaticRoute;

    if (!isTradesRoute) {
      setIsOpen(false);
      return;
    }

    // Check localStorage key
    try {
      const seen = localStorage.getItem(TRADES_EXCHANGE_ANNOUNCEMENT_STORAGE_KEY);
      if (!seen) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    } catch (e) {
      console.warn("Could not read localStorage for exchange announcement:", e);
    }
  }, [location.pathname, user, loading, isSharedProfileView, isPublicProfileView]);

  const markAsSeen = () => {
    try {
      localStorage.setItem(TRADES_EXCHANGE_ANNOUNCEMENT_STORAGE_KEY, "true");
    } catch (e) {
      console.warn("Could not set localStorage for exchange announcement:", e);
    }
    setIsOpen(false);
  };

  const handleClose = () => {
    markAsSeen();
  };

  const handleConnect = () => {
    markAsSeen();
    navigate("/exchange-trades/connect");
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200"
      data-testid="trades-exchange-announcement-overlay"
    >
      <div
        className="relative w-full max-w-[520px] max-h-[92vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0E0E10] p-5 sm:p-6 shadow-2xl text-white font-sans"
        data-testid="trades-exchange-announcement-modal"
      >
        {/* Close X Button */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-3.5 top-3.5 sm:right-4 sm:top-4 rounded-lg p-1.5 text-white/40 hover:bg-white/10 hover:text-white transition-colors"
          aria-label="Fechar anúncio"
          data-testid="announcement-modal-close-button"
        >
          <X size={18} weight="bold" />
        </button>

        <div className="space-y-4 sm:space-y-5">
          {/* Header */}
          <div className="space-y-2 pr-6">
            {/* Badge discreto */}
            <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 text-[11px] font-bold tracking-wider text-emerald-400 uppercase">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              NOVO NO GENI TRADES
            </div>

            {/* Título */}
            <h2 className="text-xl sm:text-2xl font-black tracking-tight text-white leading-snug">
              Suas operações. <br />
              <span className="text-white/90">Agora direto das corretoras.</span>
            </h2>

            {/* Descrição */}
            <p className="text-xs sm:text-sm text-white/70 leading-relaxed">
              Conecte sua corretora ou importe seu histórico. O GENI organiza suas operações automaticamente e transforma os dados em informações prontas para análise.
            </p>
          </div>

          {/* Visual flow representation: [API] [IMPORT] → GENI → ANALYTICS */}
          <div className="rounded-xl border border-white/5 bg-white/[0.02] p-2.5 sm:p-3 flex items-center justify-between gap-1 sm:gap-2 text-xs">
            <div className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-lg px-2 sm:px-2.5 py-1.5 text-white/90 font-medium">
              <Plug size={14} className="text-emerald-400" weight="bold" />
              <span className="text-[11px] sm:text-xs">API</span>
            </div>
            <span className="text-white/30 text-xs">+</span>
            <div className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-lg px-2 sm:px-2.5 py-1.5 text-white/90 font-medium">
              <UploadSimple size={14} className="text-blue-400" weight="bold" />
              <span className="text-[11px] sm:text-xs">IMPORT</span>
            </div>
            <ArrowRight size={14} className="text-emerald-400/70 shrink-0" weight="bold" />
            <div className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/25 rounded-lg px-2 sm:px-2.5 py-1.5 text-emerald-300 font-bold tracking-wide">
              <span className="text-[11px] sm:text-xs">GENI</span>
            </div>
            <ArrowRight size={14} className="text-emerald-400/70 shrink-0" weight="bold" />
            <div className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-lg px-2 sm:px-2.5 py-1.5 text-white/90 font-medium">
              <ChartBar size={14} className="text-amber-400" weight="bold" />
              <span className="text-[11px] sm:text-xs">ANALYTICS</span>
            </div>
          </div>

          {/* 3 Benefícios */}
          <div className="space-y-2.5 sm:space-y-3">
            {/* Benefício 1 */}
            <div className="flex items-start gap-3 rounded-xl border border-white/5 bg-white/[0.02] p-3 transition-colors hover:bg-white/[0.04]">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                <Plug size={16} weight="duotone" />
              </div>
              <div className="space-y-0.5">
                <h4 className="text-xs sm:text-[13px] font-bold text-white tracking-wide uppercase">
                  INTEGRAÇÃO VIA API
                </h4>
                <p className="text-[11px] sm:text-xs text-white/65 leading-relaxed">
                  Conecte uma corretora compatível e sincronize suas operações diretamente com o GENI.
                </p>
              </div>
            </div>

            {/* Benefício 2 */}
            <div className="flex items-start gap-3 rounded-xl border border-white/5 bg-white/[0.02] p-3 transition-colors hover:bg-white/[0.04]">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400">
                <UploadSimple size={16} weight="duotone" />
              </div>
              <div className="space-y-0.5">
                <h4 className="text-xs sm:text-[13px] font-bold text-white tracking-wide uppercase">
                  IMPORTAÇÃO DE HISTÓRICO
                </h4>
                <p className="text-[11px] sm:text-xs text-white/65 leading-relaxed">
                  Importe o histórico exportado da sua corretora e leve suas operações anteriores para o GENI.
                </p>
              </div>
            </div>

            {/* Benefício 3 */}
            <div className="flex items-start gap-3 rounded-xl border border-white/5 bg-white/[0.02] p-3 transition-colors hover:bg-white/[0.04]">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400">
                <ChartBar size={16} weight="duotone" />
              </div>
              <div className="space-y-0.5">
                <h4 className="text-xs sm:text-[13px] font-bold text-white tracking-wide uppercase">
                  ANÁLISE AUTOMÁTICA
                </h4>
                <p className="text-[11px] sm:text-xs text-white/65 leading-relaxed">
                  O GENI organiza e completa automaticamente as informações disponíveis das operações para alimentar seu histórico, métricas e Analytics.
                </p>
              </div>
            </div>
          </div>

          {/* Botões na parte inferior */}
          <div className="flex items-center gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1 border-white/10 bg-transparent text-white/75 hover:bg-white/5 hover:text-white rounded-xl py-2.5 text-xs sm:text-sm font-semibold transition-colors"
              data-testid="announcement-modal-dismiss-button"
            >
              Ver depois
            </Button>
            <Button
              type="button"
              onClick={handleConnect}
              className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black font-black rounded-xl py-2.5 text-xs sm:text-sm shadow-lg shadow-emerald-500/20 transition-all transform hover:-translate-y-0.5 active:translate-y-0"
              data-testid="announcement-modal-connect-button"
            >
              Conectar corretora
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}